import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5index from '@amcharts/amcharts5/index';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import am5themes_Material from '@amcharts/amcharts5/themes/Material';
import { ChartData } from '../../models/chart.models';
import { delay, Subscription } from 'rxjs';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { getServerBorderColor, getServerName, getServerTextColor, SERVERS } from '../../constants/servers.constant';

@Component({
  selector: 'app-chart',
  imports: [],
  templateUrl: './chart.component.html',
  styleUrl: './chart.component.scss',
})
export class ChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input({ required: true }) data!: { data: ChartData[]; name: string }[];
  @Input({ required: true }) timeUnit!: am5.time.TimeUnit;
  @Input({ required: true }) title!: string;

  private subscriptions: Subscription[] = [];

  @ViewChild('chartdiv') chartDiv!: ElementRef;
  private root!: am5.Root;
  private primaryColor!: string;
  private primaryColorLight!: string;
  private accentColor!: string;
  private accentColorDark!: string;
  private warnColor!: string;
  private profitColor!: string;
  private isDarkMode!: boolean;
  private isInitialized = false;

  constructor(private userSettingsService: UserSettingsService) {}

  ngAfterViewInit(): void {
    this.isInitialized = true;
    this.subscriptions.push(
      this.userSettingsService.userTheme$.pipe(delay(200)).subscribe((theme) => {
        this.updateColorsAndCreateChart();
      })
    );
    this.updateColorsAndCreateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.isInitialized && (changes['data'] || changes['timeUnit'] || changes['title'] || changes['groupCount'])) {
      this.updateColorsAndCreateChart();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    if (this.root) {
      this.root.dispose();
    }
  }

  private updateColorsAndCreateChart(): void {
    if (!this.chartDiv?.nativeElement) {
      console.warn('Chart update skipped: chartDiv not available yet.');
      return;
    }

    if (!this.data || this.data.length === 0 || !this.data[0].data.length) {
      console.warn('Chart update skipped: no data available.');
      return;
    }

    const style = getComputedStyle(this.chartDiv.nativeElement);
    this.primaryColor = style.getPropertyValue('--primary-color').trim();
    this.primaryColorLight = style.getPropertyValue('--primary-color-200').trim();
    this.accentColor = style.getPropertyValue('--accent-color').trim();
    this.accentColorDark = style.getPropertyValue('--accent-color-dark').trim();
    this.warnColor = style.getPropertyValue('--warn-color').trim();
    this.profitColor = style.getPropertyValue('--profit-color').trim();
    this.isDarkMode = style.getPropertyValue('--dark-mode').trim() === 'true';

    this.createChart();
  }

  createChart() {
    // Dispose the previous chart instance if it exists
    if (this.root) {
      this.root.dispose();
    }

    if (!this.data || this.data.length === 0 || !this.data[0].data.length) {
      console.error('No data available to plot');
      return;
    }

    // Sort the data by it's own name property
    this.data.sort((a, b) => {
      return getServerName(a.name) > getServerName(b.name) ? 1 : -1;
    });

    /* Chart code */
    // MARK: Create root element
    // https://www.amcharts.com/docs/v5/getting-started/#Root_element
    this.root = am5.Root.new(this.chartDiv.nativeElement);

    // timezone
    this.root.utc = true;

    // hide logo, we'll add it somewhere else
    this.root._logo?.dispose();

    // MARK: Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    // root.setThemes([am5themes_Animated.new(root)]);
    if (this.isDarkMode) {
      this.root.setThemes([am5themes_Dark.new(this.root)]);
    } else {
      this.root.setThemes([am5themes_Material.new(this.root)]);
    }

    // MARK: Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    let chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        layout: this.root.verticalLayout,
        focusable: true,
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0,
      })
    );

    // MARK: Title
    // https://www.amcharts.com/docs/v5/concepts/common-elements/labels/
    chart.children.unshift(
      am5.Label.new(this.root, {
        text: this.title,
        fontSize: 16,
        fontWeight: '500',
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        paddingTop: 0,
        paddingBottom: 10,
      })
    );

    // MARK: Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    let xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(this.root, {
        maxDeviation: 0.0,
        groupData: false,
        baseInterval: {
          timeUnit: this.timeUnit,
          count: 1,
        },
        fillRule: function (dataItem) {
          const axisFill = dataItem.get('axisFill');
          const date = new Date(dataItem.get('value') ?? '');
          axisFill?.setPrivate('visible', date.getUTCDay() == 6 || date.getUTCDay() == 0);
        },
        renderer: am5xy.AxisRendererX.new(this.root, {
          minorGridEnabled: false,
          minGridDistance: 50,
        }),
        tooltip: am5.Tooltip.new(this.root, {}),
      })
    );

    xAxis.get('renderer')?.axisFills.template.setAll({
      fill: this.isDarkMode ? am5.color(this.primaryColorLight) : am5.color(0xff0000),
      fillOpacity: 0.05,
      visible: true,
    });

    xAxis.get('renderer')?.labels.template.setAll({
      fontSize: 12,
    });

    xAxis.get('tooltip')?.label.setAll({
      fontSize: 12,
    });

    xAxis.get('tooltip')?.get('background')?.setAll({
      fillOpacity: 0.8,
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        maxDeviation: 0,
        renderer: am5xy.AxisRendererY.new(this.root, {}),
      })
    );

    yAxis.get('renderer')?.labels.template.setAll({
      fontSize: 12,
    });

    // MARK: Add series

    this.data.forEach((serverData, index) => {
      const serverName = getServerName(serverData.name);
      let series = chart.series.push(
        am5xy.SmoothedXLineSeries.new(this.root, {
          name: serverName,
          minBulletDistance: 10,
          connect: false,
          tension: 0.65,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value',
          valueXField: 'date',
          stroke: am5.color(this.getSeriesColor(index)),
          tooltip: am5.Tooltip.new(this.root, {
            getFillFromSprite: false,
            pointerOrientation: 'horizontal',
            labelText: '{name}: {valueY}',
          }),
        })
      );

      series.set('tooltip', this.createTooltip(series));

      series.strokes.template.setAll({
        strokeWidth: 2,
      });

      series.data.setAll(serverData.data);
    });

    // MARK: Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    let cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(this.root, {
        xAxis: xAxis,
        behavior: 'none',
      })
    );
    cursor.lineY.set('visible', false);

    // MARK: Add legend
    // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
    let legend = chart.children.push(
      am5.Legend.new(this.root, {
        width: am5.percent(100),
        paddingTop: 15,
        height: 30,
        layout: this.root.horizontalLayout,
      })
    );

    // It's is important to set legend data after all the events are set on template, otherwise events won't be copied
    legend.data.setAll(chart.series.values);

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    chart.appear(1000, 100);
  }

  // MARK: Create tooltip
  createTooltip(series: am5.Series): am5.Tooltip {
    let tooltip = am5.Tooltip.new(this.root, {
      getFillFromSprite: false,
      pointerOrientation: 'horizontal',
      labelText: '{name}: {valueY}',
    });

    tooltip.get('background')?.setAll({
      fill: series.get('stroke'),
      fillOpacity: 0.8,
    });

    tooltip.label.setAll({
      fontSize: 12,
    });

    return tooltip;
  }

  // MARK: Colors
  getSeriesColor(index: number): string {
    if (index === 0) {
      return this.isDarkMode ? this.warnColor : this.profitColor;
    }
    if (index === 1) {
      return this.isDarkMode ? this.primaryColorLight : this.primaryColor;
    }
    if (index === 2) {
      return this.isDarkMode ? this.accentColor : this.accentColorDark;
    }
    return this.warnColor;
  }
}
